import React from "react";
import {
  ShieldCheck,
  BadgeCheck,
  Zap,
  HeartHandshake,
  CalendarCheck,
} from "lucide-react";
import { useLang } from "../lib/LangContext";
import { Reveal } from "./Reveal";
import { TID } from "../lib/testIds";

const ICONS = [ShieldCheck, BadgeCheck, Zap, HeartHandshake, CalendarCheck];

export default function WhyUs() {
  const { t } = useLang();
  const items = t.why.items;

  return (
    <section
      id="why"
      data-testid={TID.whySection}
      className="relative bg-[#F8F8F9] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              — {t.why.eyebrow}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-black tracking-tight mt-4 leading-tight">
              {t.why.title}
            </h2>
          </div>
        </Reveal>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {items.map((it, i) => {
            const Icon = ICONS[i] || ShieldCheck;
            // first item large
            const className =
              i === 0
                ? "md:col-span-7 md:row-span-2 bg-black text-white"
                : i === 1
                ? "md:col-span-5 bg-white"
                : i === 2
                ? "md:col-span-5 bg-white"
                : i === 3
                ? "md:col-span-6 bg-white"
                : "md:col-span-6 bg-black text-white";
            const isDark = i === 0 || i === 4;
            return (
              <Reveal key={i} delay={i * 80} className={className}>
                <div
                  data-testid={TID.whyItem(i)}
                  className={`group relative h-full border ${
                    isDark ? "border-white/10 hover:border-[#D4AF37]/60" : "border-black/10 hover:border-black"
                  } transition-all duration-500 p-8 sm:p-10 ${i === 0 ? "min-h-[320px]" : "min-h-[200px]"}`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`p-3 inline-flex ${
                        isDark ? "bg-[#D4AF37] text-black" : "bg-black text-[#D4AF37]"
                      } transition-transform duration-500 group-hover:scale-110`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <span
                      className={`text-xs font-mono ${isDark ? "text-white/40" : "text-black/30"}`}
                    >
                      0{i + 1}
                    </span>
                  </div>

                  <h3
                    className={`mt-8 font-display tracking-tight ${
                      i === 0 ? "text-3xl sm:text-4xl" : "text-2xl"
                    }`}
                  >
                    {it.title}
                  </h3>
                  <p
                    className={`mt-3 text-base leading-relaxed ${
                      isDark ? "text-white/60" : "text-zinc-600"
                    }`}
                  >
                    {it.desc}
                  </p>

                  {i === 0 && (
                    <div className="absolute bottom-8 right-8 w-12 h-12 border border-[#E3000F] flex items-center justify-center">
                      <span className="w-4 h-1 bg-[#E3000F]" />
                      <span className="w-1 h-4 bg-[#E3000F] absolute" />
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
